import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { AppSku } from '@polaris-customerweb/sharedcomponents.core';
import {
    GarageItemsService,
    Vehicle,
    VehicleService,
    VinService,
} from '@polaris-customerweb/sharedcomponents.garage';
import {
    ImageCdnSrcDirective,
    ImageCdnSrcSetDirective,
} from '@polaris-customerweb/sharedcomponents.shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { find } from 'lodash-es';
import { take, takeWhile } from 'rxjs/operators';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { UppercaseDirective } from 'src/app/directives/uppercase.directive';
import { AppState } from 'src/app/models/app-state';
import { RemoveWhiteSpacePipe } from 'src/app/pipes/remove-whitespace.pipe';
import { StringPipe } from 'src/app/pipes/string.pipe';
import { SetActiveGarageItemId } from 'src/app/state/garage/garage.actions';
import {
    selectActiveVehicle,
    selectVehicleCategories,
    selectVehicleMakes,
    selectVehicleModels,
    selectVehicleYears,
    selectVehiclesForUser,
} from 'src/app/state/vehicle';
import {
    AddVehicleSelectCategory,
    AddVehicleSelectMake,
    AddVehicleSelectYear,
    GetVehicleCategories,
} from 'src/app/state/vehicle/vehicle.actions';
import { WebVehicleService } from 'src/app/state/vehicle/vehicle.service';

export interface AddVehicleDialogResponse {
    showConfirmation: boolean;
    skuNav: AppSku;
}

interface SelectionResult {
    id: string;
    name: string;
    imageId?: string;
    isPolaris?: boolean;
}

export const enum AddVehicleViewState {
    Start,
    CategorySelect,
    SnowMakeSelect,
    MakeModel,
    Vin,
}

@Component({
    imports: [
        AnalyticsDirective,
        AngularSvgIconModule,
        FormsModule,
        ImageCdnSrcDirective,
        ImageCdnSrcSetDirective,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        RemoveWhiteSpacePipe,
        UppercaseDirective,
        StringPipe,
    ],
    selector: 'rdx-add-vehicle',
    standalone: true,
    styleUrls: ['./add-vehicle.component.scss'],
    templateUrl: './add-vehicle.component.html',
})
export class AddVehicleComponent implements OnInit, OnDestroy {
    @Input() initialState = AddVehicleViewState.Start;
    @Input() editMode = false;

    vehicleImageUrl = '';
    vehicle: Vehicle;
    vehicles: Vehicle[];
    viewState: AddVehicleViewState;
    isIndian: boolean;
    isSlingshot: boolean;
    isOffRoad: boolean;
    isAddingVehicle = false;
    isGettingVinInfo = false;
    isDuplicateVin = false;
    isVinFinal = false;
    isPolarisVin = false;
    addingPolarisMake = false;
    vehicleType: string;
    dialogRef: MatDialogRef<AddVehicleComponent>;
    vehicleCategories: SelectionResult[];
    vehicleMakes: SelectionResult[];
    vehicleYears: number[];
    vehicleModels: SelectionResult[];
    addButtonI18n = $localize`Add`;
    saveButtonI18n = $localize`Save`;
    vehicleImageMap: { [id: string]: string } = {};
    vehicleSrcSetMap: { [id: string]: string } = {};
    vehicleMakeImageMap: { [id: string]: string } = {};
    vehicleMakeSrcSetMap: { [id: string]: string } = {};

    dropdownSetup = {
        category: false,
        make: false,
        model: false,
        year: false,
    };

    private isDestroyed = false;

    constructor(
        private readonly configService: ConfigService,
        private readonly garageItemsService: GarageItemsService,
        private readonly router: Router,
        private readonly store: Store<AppState>,
        private readonly vehicleService: VehicleService,
        private readonly vinService: VinService,
        private readonly webVehicleService: WebVehicleService
    ) {
        this.isIndian = configService.isIndian;
        this.isSlingshot = configService.isSlingshot;
        this.isOffRoad = configService.isOffRoad;
    }

    get shouldShowStartScreen(): boolean {
        return this.viewState === AddVehicleViewState.Start;
    }

    get shouldShowCategorySelect(): boolean {
        return this.viewState === AddVehicleViewState.CategorySelect;
    }

    get shouldShowSnoMakeSelect(): boolean {
        return this.viewState === AddVehicleViewState.SnowMakeSelect;
    }

    get shouldShowForm(): boolean {
        return (
            this.viewState === AddVehicleViewState.MakeModel ||
            this.viewState === AddVehicleViewState.Vin
        );
    }

    get shouldShowYearModel(): boolean {
        return this.viewState === AddVehicleViewState.MakeModel;
    }

    get shouldShowVin(): boolean {
        return this.viewState === AddVehicleViewState.Vin;
    }

    ngOnInit(): void {
        this.store.dispatch(GetVehicleCategories());
        this.store
            .pipe(
                select(selectVehiclesForUser),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((vehicles) => (this.vehicles = vehicles));

        if (this.editMode) {
            this.getActiveVehicle();
        } else {
            this.createDefaultVehicle();

            if (
                this.initialState === AddVehicleViewState.CategorySelect ||
                this.initialState === AddVehicleViewState.MakeModel
            ) {
                this.subscribeToDropdownData();
            }

            this.viewState = this.initialState;
        }

        if (this.isIndian) {
            this.vehicleType = 'bike';
        } else {
            this.vehicleType = 'vehicle';
        }
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    subscribeToDropdownData(): void {
        this.store
            .select(selectVehicleCategories)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((categories) => {
                const isEditingGem =
                    this.editMode && this.vehicle.category === 'GEM';
                this.vehicleCategories =
                    (isEditingGem
                        ? categories
                        : categories?.filter(
                              (category) => category.name !== 'GEM'
                          )) || [];

                (this.vehicleCategories || []).forEach((category) => {
                    this.vehicleImageMap[category.id] = this.getVehicleImage(
                        category.imageId ?? ''
                    );
                    this.vehicleSrcSetMap[category.id] = this.getVehicleSrcSet(
                        category.imageId ?? ''
                    );
                });

                if (this.dropdownSetup.category) {
                    this.refillCategory();
                }
            });

        this.store
            .select(selectVehicleMakes)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((makes) => {
                this.vehicleMakes = makes as SelectionResult[];

                (this.vehicleMakes || []).forEach((make) => {
                    this.vehicleMakeImageMap[make.id] = this.getMakeImage(
                        make.imageId ?? ''
                    );
                    this.vehicleMakeSrcSetMap[make.id] = this.getMakeSrcSet(
                        make.imageId ?? ''
                    );
                });
                if (makes && this.dropdownSetup.make) {
                    this.refillMake();
                }
            });

        this.store
            .select(selectVehicleYears)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((years) => {
                this.vehicleYears = years ? years : [];
                if (years && this.dropdownSetup.year) {
                    this.refillYear();
                }
            });

        this.store
            .select(selectVehicleModels)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((models) => {
                this.vehicleModels = models as SelectionResult[];
                if (models && this.dropdownSetup.model) {
                    this.refillModel();
                }
            });
    }

    setAddByMakeModel(): void {
        this.subscribeToDropdownData();
        this.viewState = AddVehicleViewState.MakeModel;
    }

    setAddByVin(): void {
        this.viewState = AddVehicleViewState.Vin;
    }

    setAddByCategory(): void {
        this.subscribeToDropdownData();
        this.viewState = AddVehicleViewState.CategorySelect;
    }

    vinChange(): void {
        this.isVinFinal = false;
        if (this.isPolarisVin) {
            // removed a valid vin, clear fields to force user to pick
            this.vehicle.category = '';
            this.vehicle.categoryId = '';
            this.vehicle.make = '';
            this.vehicle.makeId = '';
            this.vehicle.modelYear = null;
            this.vehicle.model = '';
            this.vehicle.modelId = '';
            this.vehicle.classCode = '';
            this.vehicle.modelGroup = '';
            this.vehicle.seriesCode = '';
            this.vehicle.stockImage = '';
            this.vehicle.stockImages = [];
        }
        this.isPolarisVin = false;
        this.vehicleImageUrl = '';
        if (
            this.vinService.isValidVin(this.vehicle.vin, this.vehicle.isOnRoad)
        ) {
            this.getVinInfo();
            this.isVinFinal = true;
        }
        this.isDuplicateVin = this.getIsDuplicateVin();
    }

    vinBlur(): void {
        this.isVinFinal = true;
        if (this.vehicle.vin) {
            this.getVinInfo();
        }
    }

    getVinInfo(isEditSetup = false): void {
        this.isGettingVinInfo = true;
        this.vinService
            .getVehicleSpecificationByVin(this.vehicle.vin)
            .subscribe((vinInfo) => {
                this.isGettingVinInfo = false;
                this.isPolarisVin = Boolean(vinInfo);
                this.vehicle.stockImages = vinInfo?.stockImages || [];

                if (this.isPolarisVin && vinInfo) {
                    this.vehicle =
                        this.vehicleService.setPropertiesFromVehicleSpecification(
                            this.vehicle,
                            vinInfo,
                            false
                        );
                    this.vehicleImageUrl = this.grabImageUrl(vinInfo) || '';
                    this.isPolarisVin = true;
                    this.vehicle.categoryId = '';
                    this.vehicle.makeId = '';
                }

                if (isEditSetup) {
                    if (this.isPolarisVin) {
                        this.setAddByVin();
                    } else {
                        this.setAddByMakeModel();
                    }
                }
            });
    }

    getIsDuplicateVin(): boolean {
        if (!this.vehicle.vin) {
            return false;
        }
        const vehicles = !this.editMode
            ? this.vehicles
            : this.vehicles.filter((vehicle) => vehicle.id !== this.vehicle.id);

        return vehicles.some(
            (v) => v.vin.toUpperCase() === this.vehicle.vin.toUpperCase()
        );
    }

    saveVehicle(addVehicleForm: NgForm): void {
        // adding by vin but no vin entered, switch automatically to add by make
        if (this.shouldShowVin && !this.isPolarisVin) {
            this.setAddByMakeModel();
            return;
        }
        if (
            this.isGettingVinInfo ||
            addVehicleForm.invalid ||
            this.isDuplicateVin
        ) {
            return;
        }

        if (!this.vehicle.vehicleType) {
            this.vehicle.vehicleType = this.isOffRoad ? 'ORV' : 'Motorcycle';
        }

        if (!this.vehicle.name) {
            this.vehicle.name = this.vehicle.model;
        }
        this.isAddingVehicle = true;
        if (this.editMode) {
            this.webVehicleService
                .updateVehicle(this.vehicle)
                .pipe(takeWhile(() => !this.isDestroyed))
                .subscribe(() => {
                    this.onClose();
                });
        } else {
            this.webVehicleService
                .addVehicle(this.vehicle)
                .pipe(takeWhile(() => !this.isDestroyed))
                .subscribe((vehicle) => {
                    this.isAddingVehicle = false;
                    this.onClose(false, this.calculateSku(), true);
                    this.setActiveVehicle(vehicle.id);
                });
        }
    }

    deleteVehicle(): void {
        this.onClose(true);
    }

    onClose(
        showConfirmation = false,
        skuNav: AppSku | undefined = undefined,
        vehicleAdded = false
    ): void {
        if (this.dialogRef) {
            this.dialogRef.close({
                showConfirmation,
                skuNav,
                vehicleAdded,
            } as AddVehicleDialogResponse);
        }
    }

    getVehicleImage(key: string): string {
        return `/assets/images/vehicles/${key}.png`;
    }

    getVehicleSrcSet(key: string): string {
        const basePath = '/assets/images/vehicles/';
        return `${basePath}${key}.png,
        ${basePath}${key}@2x.png 2x,
        ${basePath}${key}@3x.png 3x`;
    }

    getMakeImage(key: string): string {
        const format = key === 'polaris' ? 'svg' : 'png';
        return `/assets/images/vehicle-makes/${key}.${format}`;
    }

    getMakeSrcSet(key: string): string {
        const isPolaris = key === 'polaris';
        if (isPolaris) {
            return `/assets/images/vehicle-makes/${key}.svg`;
        } else {
            const basePath = '/assets/images/vehicle-makes/';
            return `${basePath}${key}.png,
            ${basePath}${key}@2x.png 2x,
            ${basePath}${key}@3x.png 3x`;
        }
    }

    selectCategory(categoryId: string, isSetup = false) {
        this.store.dispatch(AddVehicleSelectCategory({ categoryId }));
        this.vehicle.categoryId = categoryId;
        this.vehicle.category =
            this.vehicleCategories.find((x) => x.id === categoryId)?.name || '';
        this.vehicle.makeId = '';
        this.addingPolarisMake = false;
        this.vehicle.modelYear = isSetup ? this.vehicle.modelYear : null;
        this.vehicle.modelId = '';
        this.vehicle.stockImage = '';
        this.vehicle.stockImages = [];
        if (
            this.vehicleCategories.find((cat) => cat.id === categoryId)
                ?.name === 'Snowmobile'
        ) {
            this.viewState = AddVehicleViewState.SnowMakeSelect;
        } else {
            this.viewState = AddVehicleViewState.MakeModel;
        }

        this.setVehicleTypeByCategory();
    }

    selectMake(makeId, updateField = false, isSetup = false) {
        this.vehicle.modelYear = isSetup ? this.vehicle.modelYear : null;
        this.vehicle.modelId = '';
        const make = this.vehicleMakes.find((m) => m.id === makeId);
        this.vehicle.make = make?.name || '';
        this.addingPolarisMake = Boolean(make?.isPolaris);
        this.store.dispatch(
            AddVehicleSelectMake({
                categoryId: this.vehicle.categoryId,
                makeId,
            })
        );
        this.viewState = AddVehicleViewState.MakeModel;
        if (updateField) {
            this.vehicle.makeId = makeId;
        }
    }

    selectYear(year: number) {
        this.vehicle.modelId = '';
        this.store.dispatch(
            AddVehicleSelectYear({
                categoryId: this.vehicle.categoryId,
                makeId: this.vehicle.makeId,
                year,
            })
        );
    }

    selectModel(modelId: string) {
        this.vehicle.model =
            this.vehicleModels.find((x) => x.id === modelId)?.name || '';
    }

    private grabImageUrl(vinInfo): string {
        const image = find(vinInfo.stockImages, {
            imageStyle: 'front-right-lg',
        });

        if (image) {
            return image.url;
        } else if (this.vehicle.stockImage) {
            return this.vehicle.stockImage;
        } else {
            return this.vehicle.brandLogo;
        }
    }

    private getActiveVehicle(): void {
        this.store
            .select(selectActiveVehicle)
            .pipe(
                take(1),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((vehicle) => {
                this.vehicle = vehicle?.clone() || new Vehicle();
                if (this.vehicle.vin) {
                    this.isVinFinal = true;
                    this.getVinInfo(true);
                } else {
                    Object.entries(this.dropdownSetup).forEach(
                        (x) => (this.dropdownSetup[x[0]] = true)
                    );
                    this.setAddByMakeModel();
                }
            });
    }

    private createDefaultVehicle(): void {
        if (this.configService.isIndian) {
            this.vehicle = Vehicle.createDefaultForBrand('indian');
        } else if (this.configService.isSlingshot) {
            this.vehicle = Vehicle.createDefaultForBrand('slingshot');
        } else {
            this.vehicle = Vehicle.createDefaultForBrand('orv');
        }
    }

    private calculateSku(): AppSku | undefined {
        let skuNav: AppSku | undefined;
        // if not on indian and either:
        // - vin returned an indian
        // - selected motorcycle category
        if (
            !this.isIndian &&
            (this.vehicle.classCode === 'IND' ||
                (this.vehicle.categoryId &&
                    this.vehicle.categoryId ===
                        this.vehicleCategories?.find(
                            (x) => x.name === 'Motorcycle'
                        )?.id))
        ) {
            skuNav = 'IND';
        }
        // if not on slingshot and either:
        // - vin returned a slingshot
        // - selected slingshot category
        else if (
            !this.isSlingshot &&
            (this.vehicle.classCode === 'SLG' ||
                (this.vehicle.categoryId &&
                    this.vehicle.categoryId ===
                        this.vehicleCategories?.find(
                            (x) => x.name === 'Slingshot'
                        )?.id))
        ) {
            skuNav = 'SLG';
        }
        // if on indian or slingshot and
        // - vin not indian and
        // - vin not slingshot and
        // - did not select motorcycle category and
        // - did not select slingshot category
        else if (
            !this.isOffRoad &&
            this.vehicle.classCode !== 'IND' &&
            this.vehicle.classCode !== 'SLG' &&
            (!this.vehicle.categoryId ||
                (this.vehicle.categoryId !==
                    this.vehicleCategories?.find((x) => x.name === 'Motorcycle')
                        ?.id &&
                    this.vehicle.categoryId !==
                        this.vehicleCategories?.find(
                            (x) => x.name === 'Slingshot'
                        )?.id))
        ) {
            skuNav = 'ORV';
        }
        return skuNav;
    }

    private refillCategory() {
        this.dropdownSetup.category = false;
        const category = this.vehicleCategories.find(
            (cat) => cat.name === this.vehicle.category
        );
        if (category) {
            this.selectCategory(category.id, true);
        } else {
            this.vehicle.category = '';
            this.vehicle.make = '';
            this.vehicle.modelYear = null;
            this.vehicle.model = '';
            this.dropdownSetup.make = false;
            this.dropdownSetup.year = false;
            this.dropdownSetup.model = false;
        }
    }

    private refillMake() {
        this.dropdownSetup.make = false;
        const make = this.vehicleMakes.find(
            (m) => m.name === this.vehicle.make
        );
        this.dropdownSetup.category = false;
        if (make) {
            this.selectMake(make.id, true, true);
        } else {
            this.vehicle.make = '';
            this.vehicle.modelYear = null;
            this.vehicle.model = '';
            this.dropdownSetup.year = false;
            this.dropdownSetup.model = false;
        }
    }

    private refillYear() {
        this.dropdownSetup.year = false;
        if (this.vehicleYears.includes(this.vehicle.modelYear as number)) {
            this.selectYear(this.vehicle.modelYear as number);
        } else {
            this.vehicle.modelYear = null;
            this.vehicle.model = '';
            this.dropdownSetup.model = false;
        }
    }

    private refillModel() {
        this.dropdownSetup.model = false;
        const model = this.vehicleModels.find(
            (x) => x.name === this.vehicle.model
        );
        if (model) {
            this.selectModel(model.id);
            this.vehicle.modelId = model.id;
        } else {
            this.vehicle.model = '';
        }
    }

    private setActiveVehicle(garageItemId: string): void {
        this.store.dispatch(SetActiveGarageItemId({ garageItemId }));
        this.garageItemsService.$activeGarageItemId.set(garageItemId);
        void this.router.navigate([
            'maintenance',
            'vehicle',
            garageItemId,
            'records',
        ]);
    }

    private setVehicleTypeByCategory(): void {
        switch (this.vehicle.category) {
            case 'Side By Side':
                this.vehicle.vehicleType = 'ORV';
                break;
            case 'ATV':
                this.vehicle.vehicleType = 'ATV';
                break;
            case 'Motorcycle':
            case 'Slingshot':
                this.vehicle.vehicleType = 'Motorcycle';
                break;
            case 'Snowmobile':
            case 'Timbersled':
                this.vehicle.vehicleType = 'SnowGeneric';
                break;
            case 'GEM':
                this.vehicle.vehicleType = 'GEM';
                break;
            default:
                this.vehicle.vehicleType = 'Other';
        }
    }
}
